import { useEffect, useRef, useState } from 'react';

interface EditableProjectTitleProps {
  name: string;
  readOnly?: boolean;
  onRename: (name: string) => Promise<void>;
}

function PencilIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-3.5 w-3.5"
      aria-hidden
    >
      <path d="m2.695 14.763-1.262 3.154a.5.5 0 0 0 .65.65l3.155-1.262a4 4 0 0 0 1.343-.885L17.5 5.501a2.121 2.121 0 0 0-3-3L3.58 13.42a4 4 0 0 0-.885 1.343Z" />
    </svg>
  );
}

export default function EditableProjectTitle({
  name,
  readOnly = false,
  onRename,
}: EditableProjectTitleProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(name);
  }, [name, editing]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = async () => {
    const trimmed = draft.trim() || 'Untitled Project';
    if (trimmed === name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onRename(trimmed);
      setEditing(false);
    } catch {
      setDraft(name);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(name);
    setEditing(false);
  };

  if (readOnly) {
    return (
      <h1 className="truncate text-sm font-semibold text-text" title={name}>
        {name}
      </h1>
    );
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void commit();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        className="w-full min-w-[10rem] max-w-[18rem] rounded-md border border-kromeka-red/60 bg-surface px-2 py-0.5 text-sm font-semibold text-text outline-none ring-2 ring-kromeka-red/20"
        aria-label="Project name"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="group flex min-w-0 max-w-[18rem] items-center gap-1.5 rounded-md px-1 py-0.5 text-left transition hover:bg-surface-overlay"
      title="Click to rename project"
    >
      <h1 className="truncate text-sm font-semibold text-text group-hover:text-accent-on-dark">
        {name}
      </h1>
      <span className="shrink-0 text-text-subtle opacity-0 transition group-hover:opacity-100">
        <PencilIcon />
      </span>
    </button>
  );
}
